import { logger } from '../utils/logger'


type MessageHandler = (data: any) => void

export class WebSocketService {
  private ws: WebSocket | null = null
  private token: string | null = null
  private userId: string | null = null // Переименовано с telegramId
  private reconnectAttempts = 0
  private timeout: any = null
  private heartbeatInterval: any = null
  private connectionTimeout: any = null
  private isConnected = false
  private handlers = new Map<string, MessageHandler[]>()
  private lastPongTime: number = 0
  
  // Singleton pattern
  private static instance: WebSocketService | null = null

  constructor(private readonly buildUrl: (id: string, token: string) => string) {}
  
  static getInstance(buildUrl: (id: string, token: string) => string): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService(buildUrl)
    }
    return WebSocketService.instance
  }

  connect(userId: string, token: string) {
    console.log('🔥 ОТЛАДКА WebSocketService.connect(): Попытка подключения userId:', userId, 'token:', token?.substring(0, 10) + '...')
    
    // 🔥 ИСПРАВЛЕНИЕ: Улучшенная проверка токена ПЕРЕД подключением
    if (!token) {
      logger.warn('⚠️ WebSocket: Токен не предоставлен, отменяем подключение')
      return
    }
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const now = Math.floor(Date.now() / 1000)
      
      // Проверяем истечение токена с запасом в 30 секунд
      if (payload.exp && payload.exp <= (now + 30)) {
        const expiredSeconds = now - payload.exp
        logger.warn(`⚠️ WebSocket: Токен истек или скоро истечет (exp: ${payload.exp}, now: ${now}, истек ${expiredSeconds}s назад)`)
        console.log('🔥 ОТЛАДКА: Токен истек/истекает, не подключаемся')
        
        // 🔥 КРИТИЧНО: Очищаем истекший токен и уведомляем о необходимости обновления
        this.token = null
        this.userId = null
        
        // Уведомляем о необходимости обновления токена
        this.emit('token_expired', { 
          userId, 
          expiredAt: payload.exp, 
          now,
          expiredSeconds 
        })
        
        return
      }
      
      const timeLeft = payload.exp - now
      logger.debug(`✅ WebSocket: Токен валиден (exp: ${payload.exp}, now: ${now}, осталось: ${timeLeft}s)`)
      
      // Предупреждаем если токен скоро истечет (менее 5 минут)
      if (timeLeft < 300) {
        logger.warn(`⚠️ WebSocket: Токен скоро истечет через ${timeLeft}s`)
        this.emit('token_expiring_soon', { userId, timeLeft })
      }
      
    } catch (e) {
      logger.warn(`⚠️ WebSocket: Не удалось проверить токен, отменяем подключение: ${e}`)
      return
    }
    
    // Предотвращаем множественные подключения с теми же параметрами
    if (this.userId === userId && this.token === token && this.isConnected) {
      console.log('🔥 ОТЛАДКА: WebSocket уже подключен, пропускаем')
      logger.debug('🔗 WebSocket уже подключен с теми же параметрами, пропускаем')
      return
    }
    
    this.userId = userId
    this.token = token
    
    // Закрываем предыдущее соединение
    if (this.ws) {
      logger.debug('🔌 Закрываем предыдущее WebSocket соединение')
      this.close()
    }
    
    const url = this.buildUrl(userId, token)
    console.log('🔥 ОТЛАДКА: Создаем WebSocket соединение к URL:', url)
    logger.debug('🔗 Подключение к WebSocket:', url)
    
    this.ws = new WebSocket(url)

    // Устанавливаем таймаут подключения
    this.connectionTimeout = setTimeout(() => {
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        logger.error('❌ WebSocket: Таймаут подключения')
        this.ws.close()
        this.scheduleReconnect()
      }
    }, 10000)

    this.ws.onopen = () => {
      logger.log('✅ WebSocket подключен')
      this.isConnected = true
      this.reconnectAttempts = 0
      
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout)
        this.connectionTimeout = null
      }
      
      // Запускаем heartbeat
      this.startHeartbeat()
      
      this.emit('connection_established', { connected: true })
    }

    this.ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data)
        
        // Обрабатываем системные сообщения
        if (data.type === 'connection_established') {
          logger.debug('WebSocket connection established', data)
          this.emit('connection_established', data)
        } else if (data.type === 'pong') {
          // Ответ на ping - соединение живо
          this.lastPongTime = Date.now()
        } else {
          // Обрабатываем все остальные сообщения
          this.emit(data?.type || 'message', data)
        }
      } catch (error) {
        logger.error('❌ Ошибка парсинга WebSocket сообщения:', error)
        this.emit('message', ev.data)
      }
    }

    this.ws.onerror = (error) => {
      logger.error('❌ WebSocket ошибка:', error)
      this.isConnected = false
      this.emit('error', error)
      this.scheduleReconnect()
    }

    this.ws.onclose = (event) => {
      logger.log('🔌 WebSocket закрыт:', event.code, event.reason)
      this.isConnected = false
      this.stopHeartbeat()
      
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout)
        this.connectionTimeout = null
      }
      
      this.emit('close', event)
      
      // 🔥 ИСПРАВЛЕНИЕ: Не переподключаемся если токен истек
      if (event.code === 4002) {
        logger.warn('⚠️ WebSocket: Соединение закрыто из-за истекшего токена, не переподключаемся')
        console.log('🔥 ОТЛАДКА: Токен истек, очищаем и не переподключаемся')
        // Очищаем истекший токен
        this.token = null
        this.userId = null
        return
      }
      
      this.scheduleReconnect()
    }
  }

  private startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({
          type: 'ping',
          timestamp: new Date().toISOString()
        })
      }
    }, 30000) // Каждые 30 секунд
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  private scheduleReconnect() {
    if (!this.userId || !this.token) {
      logger.debug('🔄 WebSocket: Нет userId или token для переподключения')
      return
    }

    // 🔥 ИСПРАВЛЕНИЕ: Улучшенная проверка токена перед переподключением
    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]))
      const now = Math.floor(Date.now() / 1000)
      
      // Проверяем с запасом в 30 секунд
      if (payload.exp && payload.exp <= (now + 30)) {
        const expiredSeconds = now - payload.exp
        logger.warn(`⚠️ WebSocket: Токен истек/истекает (exp: ${payload.exp}, now: ${now}, истек ${expiredSeconds}s назад), отменяем переподключение`)
        
        // 🔥 ИСПРАВЛЕНИЕ: Очищаем истекший токен и уведомляем
        this.token = null
        this.userId = null
        
        // Уведомляем о необходимости обновления токена
        this.emit('token_expired', { 
          userId: this.userId, 
          expiredAt: payload.exp, 
          now,
          expiredSeconds,
          context: 'reconnect'
        })
        
        return
      }
    } catch (e) {
      logger.warn(`⚠️ WebSocket: Не удалось проверить токен, отменяем переподключение: ${e}`)
      return
    }

    this.reconnectAttempts += 1
    // Увеличиваем задержку для снижения нагрузки на сервер
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000) // Экспоненциальная задержка до 30 секунд
    
    logger.log(`🔄 WebSocket: Переподключение через ${delay}ms (попытка ${this.reconnectAttempts})`)

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      if (this.userId && this.token) {
        this.connect(this.userId, this.token)
      }
    }, delay)
  }

  on(type: string, handler: MessageHandler) {
    const arr = this.handlers.get(type) || []
    arr.push(handler)
    this.handlers.set(type, arr)
  }

  off(type: string, handler: MessageHandler) {
    const arr = this.handlers.get(type) || []
    this.handlers.set(
      type,
      arr.filter((h) => h !== handler)
    )
  }

  private emit(type: string, data: any) {
    const arr = this.handlers.get(type) || []
    arr.forEach((h) => h(data))
  }

  send(obj: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj))
    }
  }

  close() {
    logger.log('🔌 Закрытие WebSocket соединения')
    this.isConnected = false
    
    clearTimeout(this.timeout)
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout)
      this.connectionTimeout = null
    }
    
    this.stopHeartbeat()
    
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      readyState: this.ws?.readyState || WebSocket.CLOSED
    }
  }
}
